// src/components/ThemeToggle.jsx with added onMouseEnter and onMouseLeave props
import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";

const ThemeToggle = ({ theme, setTheme, colorTheme, setColorTheme, onMouseEnter, onMouseLeave }) => {
  const [showThemeOptions, setShowThemeOptions] = useState(false);
  const themeRef = useRef(null);
  
  // Theme options with emojis
  const themeOptions = [
    { value: "mint", label: "Mint", emoji: "🌿" },
    { value: "pink", label: "Pink", emoji: "🌸" },
    { value: "lavender", label: "Lavender", emoji: "💜" },
  ];
  
  // Check for user preference for dark mode
  useEffect(() => {
    const isDark = localStorage.getItem("darkMode") === "true" || 
      (!localStorage.getItem("darkMode") && 
      window.matchMedia("(prefers-color-scheme: dark)").matches);
    
    setColorTheme(isDark ? "dark" : "light");
    document.documentElement.classList.toggle("dark", isDark);
  }, [setColorTheme]);
  
  // Toggle between light and dark mode
  const toggleDarkMode = () => {
    const newMode = colorTheme === "light" ? "dark" : "light";
    setColorTheme(newMode);
    localStorage.setItem("darkMode", newMode === "dark");
    document.documentElement.classList.toggle("dark", newMode === "dark");
    
    // Close theme options when toggling mode
    setShowThemeOptions(false);
  };
  
  // Toggle theme options dropdown
  const toggleThemeOptions = () => {
    setShowThemeOptions(!showThemeOptions);
  };
  
  // Close theme options when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (themeRef.current && !themeRef.current.contains(event.target)) {
        setShowThemeOptions(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [themeRef]);
  
  return (
    <div className="relative" ref={themeRef}>
      <motion.button
        onClick={toggleThemeOptions}
        className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white flex items-center"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        aria-label="Theme settings"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <span className="text-xl mr-1">
          {colorTheme === "light" ? "☀️" : "🌙"}
        </span>
        <span className="text-xs">▼</span>
      </motion.button>
      
      {/* Theme options dropdown */}
      {showThemeOptions && (
        <motion.div 
          className="absolute right-0 mt-2 bg-white dark:bg-gray-800 shadow-lg rounded-xl p-2 z-10"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
        >
          {/* Light/Dark Mode */}
          <button
            onClick={toggleDarkMode}
            className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
          >
            <span className="mr-2">{colorTheme === "light" ? "🌙" : "☀️"}</span>
            <span className="dark:text-white">{colorTheme === "light" ? "Dark Mode" : "Light Mode"}</span>
          </button>
          
          <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
          <p className="text-xs text-gray-500 dark:text-gray-400 px-3 py-1">Color Theme</p>
          
          {/* Color theme options */}
          {themeOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => {
                setTheme(option.value);
                setShowThemeOptions(false);
              }}
              className={`w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center ${
                theme === option.value ? "font-semibold" : ""
              }`}
            >
              <span className="mr-2">{option.emoji}</span>
              <span className="dark:text-white">{option.label}</span>
            </button>
          ))}
        </motion.div>
      )}
    </div>
  );
};

export default ThemeToggle;
